import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useUpsellDialog } from "../contexts/UpsellDialogContext";
import { usePaymentUpdateDialog } from "../contexts/PaymentUpdateDialogContext";
import { useToast } from "../hooks/use-toast";
import { CheckIcon, Loader2 } from "lucide-react";
import { useState } from "react";
import { useAuthProtected } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
  createDirectCheckoutSessionFunction,
  createOneTimeCheckoutSession,
} from "../data/firebase";
import { motion } from "framer-motion";

const includedFeatures = [
  "Generate replies",
  "Extract texts from screenshots",
  "Analyze every chat",
  "Realtime personalized coaching",
  "Battle-tested replies",
  "Built by real dating coaches enhanced by AI",
];

// Payment type toggle component moved outside to prevent recreation on re-renders
const PaymentTypeToggle = ({
  paymentType,
  setPaymentType,
}: {
  paymentType: "subscription" | "oneTime";
  setPaymentType: (type: "subscription" | "oneTime") => void;
}) => (
  <div className="flex items-center justify-center p-4">
    <div
      className="relative bg-gray-200 rounded-full p-0.5 w-[240px] h-10 sm:w-[320px] sm:h-12 cursor-pointer"
      onClick={() =>
        setPaymentType(
          paymentType === "subscription" ? "oneTime" : "subscription"
        )
      }
    >
      <motion.div
        className="absolute top-0.5 left-0.5 w-[118px] h-9 sm:w-[158px] sm:h-11 bg-white rounded-full shadow-md"
        animate={{
          x: paymentType === "subscription" ? 0 : "calc(100% - 1px)",
        }}
        transition={{
          type: "tween",
          ease: "easeInOut",
          duration: 0.3,
        }}
      />
      <span
        className={`absolute left-0.5 inset-y-0 w-[118px] sm:w-[158px] flex items-center justify-center z-10 text-sm sm:text-base font-medium transition-colors duration-300 ${
          paymentType === "subscription" ? "text-gray-800" : "text-gray-500"
        }`}
      >
        Subscription
      </span>
      <span
        className={`absolute right-0.5 inset-y-0 w-[118px] sm:w-[158px] flex items-center justify-center z-10 text-sm sm:text-base font-medium transition-colors duration-300 ${
          paymentType === "oneTime" ? "text-gray-800" : "text-gray-500"
        }`}
      >
        One-Time
      </span>
    </div>
  </div>
);

export function UpsellDialog() {
  const { isUpsellOpen, closeUpsell } = useUpsellDialog();
  const [paymentType, setPaymentType] = useState<"subscription" | "oneTime">(
    "subscription"
  );
  const [isLoading, setIsLoading] = useState(false);
  const { refreshCustomClaims } = useAuthProtected();
  const { toast } = useToast();
  const { openPaymentUpdate } = usePaymentUpdateDialog();

  // Server refuses to start a second subscription when one already exists.
  // Returns true if the error was handled here.
  const handleSubscriptionConflict = async (error: unknown): Promise<boolean> => {
    const code = (error as { details?: { code?: string } })?.details?.code;
    if (code === "subscription_active") {
      // Access state was stale; the server just re-synced the claims.
      await refreshCustomClaims();
      closeUpsell();
      toast({
        title: "You're already subscribed",
        description: "Your subscription is active. No new charge was made.",
      });
      return true;
    }
    if (code === "subscription_past_due") {
      closeUpsell();
      openPaymentUpdate();
      toast({
        title: "Payment needs attention",
        description:
          "Your last payment failed. Update your payment method to restore access.",
        variant: "destructive",
      });
      return true;
    }
    return false;
  };

  const handleSubscribe = async () => {
    setIsLoading(true);
    try {
      const { data } = await createDirectCheckoutSessionFunction();
      // console.log(data);
      window.location.href = (data as { url: string }).url;
    } catch (error) {
      console.error("Error creating checkout session:", error);
      if (await handleSubscriptionConflict(error)) return;
      toast({
        title: "Couldn't start checkout",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOneTimeSubscribe = async () => {
    setIsLoading(true);
    try {
      const { data } = await createOneTimeCheckoutSession();
      window.location.href = (data as { url: string }).url;
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast({
        title: "Couldn't start checkout",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isUpsellOpen} onOpenChange={closeUpsell}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-scroll">
        <div className="bg-white">
          <div className="mx-auto py-4 sm:py-8 px-0 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-4xl sm:text-center flex flex-col items-center gap-4">
              <div className="rounded-full bg-gradient-to-br from-gray-900 to-gray-600 px-4 py-1 text-sm font-semibold text-white w-fit">
                <p className="text-center text-xl font-bold">Premium</p>
              </div>
              <h2 className="text-pretty text-2xl sm:text-4xl font-semibold tracking-tight text-gray-900">
                Get Access to Options AI
              </h2>
              <p className="mx-auto max-w-2xl text-pretty text-lg font-medium text-gray-500 sm:text-xl/8">
                Your access has expired - buy another month pass, or subscribe
                and save! Choose the plan that's right for you:
              </p>
            </div>
            <div className="mt-8 mx-auto max-w-2xl rounded-3xl ring-1 ring-gray-200 lg:mx-0 flex lg:max-w-none flex-col-reverse sm:flex-row">
              <div className="p-4 sm:p-8 lg:flex-auto">
                <h3 className="text-xl sm:text-3xl font-semibold tracking-tight text-gray-900">
                  Options AI Premium
                </h3>
                <p className="mt-6 text-base/7 text-gray-600">
                  Get full access to the only AI wingman that wasn't built by
                  nerds. Options uses battle-tested dating formulas supercharged
                  by cutting edge GPT. Personalized for you. Maximized for
                  success.
                </p>
                <div className="mt-10 flex items-center gap-x-4">
                  <h4 className="flex-none text-sm/6 font-semibold text-purple-600">
                    What's included
                  </h4>
                  <div className="h-px flex-auto bg-gray-100" />
                </div>
                <ul
                  role="list"
                  className="mt-8 grid grid-cols-1 gap-4 text-sm/6 text-gray-600 sm:grid-cols-2 sm:gap-6"
                >
                  {includedFeatures.map((feature) => (
                    <li key={feature} className="flex gap-x-3">
                      <CheckIcon
                        aria-hidden="true"
                        className="h-6 w-5 flex-none text-purple-600"
                      />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-2 lg:mt-0 lg:w-full lg:max-w-md lg:shrink-0">
                <div className="rounded-2xl bg-gray-50 py-12 sm:py-16 ring-1 ring-inset ring-gray-900/5 lg:py-16 h-full">
                  <div className="h-full px-4 sm:px-8 flex flex-col items-center justify-center w-full gap-3">
                    <PaymentTypeToggle
                      paymentType={paymentType}
                      setPaymentType={setPaymentType}
                    />
                    <div className="relative flex flex-col items-start justify-start gap-3 sm:gap-4 w-full">
                      <p className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 min-h-[2.5rem] sm:min-h-[3.5rem] flex items-center">
                        {paymentType === "subscription"
                          ? "Subscribe & Save"
                          : "1 Month Access Pass"}
                      </p>
                      <p className="text-lg sm:text-xl font-semibold tracking-tight text-gray-700 min-h-[1.75rem] flex items-center">
                        {paymentType === "subscription"
                          ? "Options AI Premium monthly"
                          : "One time payment"}
                      </p>
                      <p className="text-lg sm:text-xl font-medium tracking-tight text-gray-700 min-h-[1.75rem] flex items-center">
                        {paymentType === "subscription" ? "$19 / month" : "$29"}
                      </p>
                      <Button
                        variant="custom"
                        className="rounded-md bg-purple-600 px-4 sm:px-12 py-2 text-center text-base sm:text-sm font-semibold text-white shadow-sm hover:bg-purple-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 hover:text-white w-full flex flex-row items-center justify-center gap-2 "
                        onClick={
                          paymentType === "subscription"
                            ? handleSubscribe
                            : handleOneTimeSubscribe
                        }
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Please wait...
                          </>
                        ) : paymentType === "subscription" ? (
                          "Get Premium"
                        ) : (
                          "Get 1 Month Pass"
                        )}
                      </Button>
                      <p className="text-xs text-gray-500 text-left w-64 min-h-[2rem] flex items-center">
                        {paymentType === "subscription"
                          ? "Cancel anytime in 1 click or email support. You will be billed $19 monthly."
                          : "Pay-as-you-go 30 days access"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
